import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  ACCESS_ASSIGNMENTS_KEY, ACCESS_ROLES_KEY, deleteRole, fetchAssignments, fetchPermissions, fetchRoles,
  type AccessRole,
} from "@/lib/accessControl";
import { RoleEditorDialog } from "./RoleEditorDialog";

export function AccessRolesTab({ canManage }: { canManage: boolean }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<AccessRole | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [deleting, setDeleting] = useState<AccessRole | null>(null);

  const { data: roles, isLoading, error } = useQuery({ queryKey: ACCESS_ROLES_KEY, queryFn: fetchRoles });
  const { data: permissions } = useQuery({ queryKey: ["admin", "access", "permissions"], queryFn: fetchPermissions });
  const { data: assignments } = useQuery({ queryKey: ACCESS_ASSIGNMENTS_KEY, queryFn: fetchAssignments });

  const holders = useMemo(() => {
    const counts = new Map<string, number>();
    for (const a of assignments ?? []) counts.set(a.role_id, (counts.get(a.role_id) ?? 0) + 1);
    return counts;
  }, [assignments]);
  const labelFor = useMemo(() => new Map((permissions ?? []).map((p) => [p.key, p.label])), [permissions]);

  const remove = useMutation({
    mutationFn: (id: string) => deleteRole(id),
    onSuccess: () => {
      toast.success("Role deleted");
      qc.invalidateQueries({ queryKey: ["admin", "access"] });
    },
    onError: (e: Error) => toast.error(e.message),
    onSettled: () => setDeleting(null),
  });

  const openEditor = (role: AccessRole | null) => {
    setEditing(role);
    setEditorOpen(true);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-muted-foreground">
          Built-in roles keep the access they have today. Custom roles are made of the permissions you pick.
        </p>
        {canManage && (
          <Button onClick={() => openEditor(null)} className="shrink-0">
            <Plus className="h-4 w-4 mr-1" /> New role
          </Button>
        )}
      </div>

      {isLoading ? (
        <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : error ? (
        <p className="text-sm text-destructive">Could not load roles: {(error as Error).message}</p>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {(roles ?? []).map((role) => {
            const count = holders.get(role.id) ?? 0;
            return (
              <Card key={role.id}>
                <CardHeader className="space-y-1">
                  <div className="flex items-start justify-between gap-2">
                    <CardTitle className="text-lg flex flex-wrap items-center gap-2">
                      {role.name}
                      {role.is_system && <Badge variant="outline">Built-in</Badge>}
                    </CardTitle>
                    {canManage && !role.is_system && (
                      <div className="flex shrink-0">
                        <Button variant="ghost" size="icon" aria-label={`Edit ${role.name}`} onClick={() => openEditor(role)}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" aria-label={`Delete ${role.name}`} onClick={() => setDeleting(role)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                  {role.description && <CardDescription>{role.description}</CardDescription>}
                </CardHeader>
                <CardContent className="space-y-3">
                  <p className="text-sm text-muted-foreground">
                    {count === 1 ? "1 person" : `${count} people`}
                  </p>
                  {role.is_system ? (
                    <p className="text-sm text-muted-foreground">
                      {role.key === "admin" ? "Every permission." : "Access comes from built-in rules."}
                    </p>
                  ) : role.permissions.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No permissions yet.</p>
                  ) : (
                    <div className="flex flex-wrap gap-1.5">
                      {role.permissions
                        .map((k) => labelFor.get(k) ?? k)
                        .sort()
                        .map((label) => <Badge key={label} variant="secondary">{label}</Badge>)}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <RoleEditorDialog
        open={editorOpen}
        onOpenChange={setEditorOpen}
        role={editing}
        permissions={permissions ?? []}
      />

      <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {deleting?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              {(holders.get(deleting?.id ?? "") ?? 0) > 0
                ? `${holders.get(deleting!.id)} people will lose this role. `
                : ""}
              This can't be undone, but the change is recorded in the access log.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={remove.isPending}
              onClick={(e) => {
                e.preventDefault();
                if (deleting) remove.mutate(deleting.id);
              }}
            >
              {remove.isPending && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
              Delete role
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
